import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'
import React, { ReactNode, useEffect, useState } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn'
import { Link } from 'react-router-dom'

// Колонки, которые не оборачиваются в ссылку (интерактивные элементы)
const EXCLUDED_LINK_COLUMNS = ['select', 'actions', 'action', 'id', 'expand']

interface DataTableProps<TData extends { id: string }, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  expandableRows?: boolean
  expandableChildren?: ReactNode | ((row: TData) => ReactNode)
  isRowLink?: boolean
  navigateTo?: string
  onClickLink?: (id: string) => void
  onTableReady?: (table: ReturnType<typeof useReactTable<TData>>) => void
}

export function TableTemplate<TData extends { id: string }, TValue>({
  columns,
  data,
  expandableRows = false,
  expandableChildren,
  isRowLink,
  navigateTo,
  onClickLink,
  onTableReady,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
  })

  useEffect(() => {
    onTableReady?.(table)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table])

  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  const toggleRow = (index: number, event: React.MouseEvent) => {
    event.stopPropagation()
    setExpandedRow(expandedRow === index ? null : index)
  }

  const getRowUrl = (id: string) => {
    return navigateTo ? `/${navigateTo}/${id}` : id
  }

  return (
    <div className="overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="text-text-secondary">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => {
              const rowUrl = getRowUrl(row.original.id)
              const handleCellClick = onClickLink ? () => onClickLink(row.original.id) : undefined

              return (
                <React.Fragment key={row.id}>
                  <TableRow data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => {
                      const isExcluded = EXCLUDED_LINK_COLUMNS.includes(cell.column.id)
                      const shouldWrapInLink = isRowLink && !isExcluded

                      // Для expand колонки — особая обработка
                      if (cell.column.id === 'expand' && expandableRows && expandableChildren) {
                        return (
                          <TableCell key={cell.id}>
                            <button onClick={(event) => toggleRow(index, event)} className="mr-2">
                              <ChevronDown
                                className={`transition-transform ${
                                  expandedRow === index ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                          </TableCell>
                        )
                      }

                      // Ячейка со ссылкой
                      if (shouldWrapInLink) {
                        return (
                          <TableCell key={cell.id} className="p-0">
                            <Link
                              to={rowUrl}
                              draggable={false}
                              className="flex w-full h-full items-center p-2 min-h-[inherit] no-underline text-inherit hover:no-underline"
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Link>
                          </TableCell>
                        )
                      }

                      // Обычная ячейка (excluded или без isRowLink)
                      return (
                        <TableCell
                          key={cell.id}
                          onClick={handleCellClick}
                          className={handleCellClick ? 'cursor-pointer' : ''}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      )
                    })}
                  </TableRow>

                  {expandableRows && expandedRow === index && expandableChildren && (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="p-4">
                        {typeof expandableChildren === 'function'
                          ? expandableChildren(row.original)
                          : expandableChildren}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              )
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center"></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}